import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './app/hooks';
import Header from './components/Layout/Header';
import LoadingIndicator from './components/LoadingIndicator';
import { IDLE_TIME } from './constants';
import useTaskRouterWS from './hooks/taskRouterWS/useTaskRouterWS.hooks';
import useUnleashHooks from './hooks/unleash/useUnleash.hooks';
import useAppIdleTimer from './hooks/useAppIdleTimer.hooks';
import useExternalOrderHandler from './hooks/useExternalOrderHandler.hooks';
import { refreshAuthToken } from './reducers/userSlice';
import {
  selectIsFetchingTaskStatus,
  selectIsLoading,
} from './redux/features/loading/loading.selector';
import { selectCurrentTaskAIStatus } from './redux/features/taskRouter/taskRouter.selector';
import { selectActiveRestaurantDetails } from './selectors/restaurant';
import logger from './utils/logger';
import { startMainThreadStallMonitor } from './utils/mainThreadStallMonitor';
import useLogout from './components/Login/useLogout';
import useHealthScore from './hooks/useHealthScore.hooks';
import useOTELMetrics from './hooks/useOTELMetrics.hooks';
import { useBuildNumberCheck } from './hooks/useBuildNumberCheck/useBuildNumberCheck.hooks';
import RefreshBanner from './components/RefreshBanner';
import { DisplayPreferenceProvider } from './components/Layout/DisplayPreferenceContext';
import { LayoutPreferenceProvider } from './components/Layout/LayoutPreferenceContext';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  appClass: {
    height: '100vh',
  },
  containerClass: {
    height: `calc(100% - ${spacing(8)})`,
  },
}));

function App() {
  const { appClass, containerClass } = useStyles();
  const { healthMetric, stopSendingMetrics } = useOTELMetrics();
  const { logout } = useLogout({ stopSendingMetrics });
  useAppIdleTimer({ logout });
  useExternalOrderHandler();
  useUnleashHooks();
  const { sendHealthScore } = useHealthScore(healthMetric);
  const {
    handleAgentAvailability,
    sendTaskMessage,
    sendAgentEvent,
    initializeTaskRouter,
    isTaskAssignmentMode,
  } = useTaskRouterWS({ logout });
  const isLoading = useAppSelector(selectIsLoading);
  const isFetchingTaskStatus = useAppSelector(selectIsFetchingTaskStatus);
  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);
  const { restaurantName } = useAppSelector(selectActiveRestaurantDetails);
  const dispatch = useAppDispatch();

  //NOTE: This is a temporary fix. Current JWT token expiry is 60 minutes, refreshing JWT token every 30 minutes so that 401 errors doesn't occur
  useEffect(() => {
    let refreshTimer = setInterval(() => {
      logger.debug({ message: 'Refreshing auth token for every 30 minutes' });
      dispatch(refreshAuthToken({ logout }));
    }, IDLE_TIME / 2);
    return () => {
      clearInterval(refreshTimer);
    };
  }, []);

  // HITL-1747: log main-thread stalls (a stall freezes the console and the
  // guest audio together). Logs only; one observer for the app's lifetime.
  useEffect(() => {
    startMainThreadStallMonitor();
  }, []);

  const { updateAvailable, refreshApp, ignoreUpdate } = useBuildNumberCheck();

  return (
    // Outside the layout provider: that one hosts the dialog, whose Display tab
    // reads and saves through this one (HITL-1695).
    <DisplayPreferenceProvider>
      <LayoutPreferenceProvider>
        <div data-testid="app" className={appClass}>
          {updateAvailable && (
            <RefreshBanner onRefresh={refreshApp} onIgnore={ignoreUpdate} />
          )}
          <Header
            handleAgentAvailability={handleAgentAvailability}
            sendAgentEvent={sendAgentEvent}
            logout={logout}
            sendHealthScore={sendHealthScore}
          />
          <div data-testid="app-detail" className={containerClass}>
            {isLoading ? (
              <LoadingIndicator
                restaurantName={restaurantName}
                isFetchingTaskStatus={isFetchingTaskStatus}
                aiStatus={currentTaskAIStatus}
              />
            ) : (
              <Outlet
                context={{
                  handleAgentAvailability,
                  sendTaskMessage,
                  initializeTaskRouter,
                  isTaskAssignmentMode,
                }}
              />
            )}
          </div>
        </div>
      </LayoutPreferenceProvider>
    </DisplayPreferenceProvider>
  );
}

export default App;
